<template>
  <div class="form-step">
    <div class="section-header d-flex justify-content-between align-items-center">
      <h5 class="section-title">Grants ({{ localForm.grants.length }})</h5>
      <button type="button" class="btn btn-outline-primary" @click="addGrant">
        <i class="bi bi-plus me-2"></i>Add Grant
      </button>
    </div>

    <div v-if="grantDraft" class="grant-item mb-4 p-3 border rounded bg-light-subtle">
      <div class="row my-3">
        <div class="col-md-12">
          <label class="form-label">Title</label>
          <input ref="newGrantTitleInput" type="text" class="form-control" :class="{ 'field-invalid': isGrantFieldInvalid(grantDraft, 'title', null, true) }" v-model="grantDraft.title" placeholder="Grant title" @input="handleGrantDraftInput">
        </div>
      </div>
      <div class="row mb-3">
        <div class="col-md-4">
          <label class="form-label">Code</label>
          <input type="text" class="form-control" :class="{ 'field-invalid': isGrantFieldInvalid(grantDraft, 'code', null, true) }" v-model="grantDraft.code" placeholder="Grant code" @input="handleGrantDraftInput">
        </div>
        <div class="col-md-4">
          <label class="form-label">Select role...</label>
          <select class="form-select" :class="{ 'field-invalid': isGrantFieldInvalid(grantDraft, 'role', null, true) }" v-model="grantDraft.role" @change="handleGrantDraftInput">
            <option value="">Select role...</option>
            <option value="Principal Investigator (PI)">Principal Investigator (PI)</option>
            <option value="Co-Investigator(s)">Co-Investigator(s)</option>
            <option value="Clinical Research Coordinator (CRC)">Clinical Research Coordinator (CRC)</option>
            <option value="Clinical Research Associate (CRA)">Clinical Research Associate (CRA)</option>
            <option value="Data Manager">Data Manager</option>
            <option value="Biostatistician">Biostatistician</option>
            <option value="Regulatory Affairs Specialist">Regulatory Affairs Specialist</option>
            <option value="Clinical Trial Manager">Clinical Trial Manager</option>
            <option value="Clinical Trial Monitor">Clinical Trial Monitor</option>
            <option value="Pharmacist">Pharmacist</option>
            <option value="Patient Recruiter/Enroller">Patient Recruiter/Enroller</option>
            <option value="Quality Assurance Specialist">Quality Assurance Specialist</option>
            <option value="Study Nurse">Study Nurse</option>
            <option value="Other">Other</option>
          </select>
        </div>
        <div class="col-md-4">
          <label class="form-label">Amount</label>
          <input type="number" class="form-control" v-model.number="grantDraft.amount" placeholder="0.00" step="0.01">
        </div>
      </div>
      <div class="row mb-3">
        <div class="col-md-12">
          <label class="form-label">Source of funding</label>
          <select class="form-select" :class="{ 'field-invalid': isGrantFieldInvalid(grantDraft, 'fundingSource', null, true) }" :value="getFundingSourceSelectValue(grantDraft)" @change="handleFundingSourceSelectChange(grantDraft, $event.target.value)">
            <option value="">Select funding source</option>
            <option v-for="option in fundingSourceOptions" :key="option" :value="option">{{ option }}</option>
            <option value="__other__">Other</option>
          </select>
          <input v-if="isFundingSourceOther(grantDraft)" type="text" class="form-control mt-2" :class="{ 'field-invalid': isGrantFieldInvalid(grantDraft, 'fundingSource', null, true) }" v-model="grantDraft.fundingSource" placeholder="Specify other funding source" @input="handleGrantDraftInput">
          <small v-if="isFundingSourceOther(grantDraft)" class="text-muted d-block mt-1">
            Enter the funding source name for the Other option.
          </small>
        </div>
      </div>
      <div class="row mb-3">
        <div class="col-md-4">
          <label class="form-label">Currency</label>
          <select class="form-select" :class="{ 'field-invalid': isGrantFieldInvalid(grantDraft, 'currency', null, true) }" v-model="grantDraft.currency" @change="handleGrantDraftInput">
            <option value="">Select currency</option>
            <option value="EUR">EUR</option>
            <option value="USD">USD</option>
            <option value="GBP">GBP</option>
          </select>
        </div>
        <div class="col-md-4">
          <label class="form-label">Start year</label>
          <input type="number" class="form-control" :class="{ 'field-invalid': isGrantFieldInvalid(grantDraft, 'startYear', null, true) }" v-model.number="grantDraft.startYear" :min="1900" :max="2100" @input="handleGrantDraftInput">
        </div>
        <div class="col-md-4">
          <label class="form-label">End year</label>
          <input type="number" class="form-control" :class="{ 'field-invalid': isGrantFieldInvalid(grantDraft, 'endYear', null, true) }" v-model.number="grantDraft.endYear" :min="1900" :max="2100" @input="handleGrantDraftInput">
        </div>
      </div>
      <div class="row">
        <div class="col-md-12 d-flex justify-content-end gap-2">
          <button type="button" class="btn btn-outline-secondary" @click="cancelNewGrant">Cancel</button>
          <button type="button" class="btn btn-outline-primary" @click="saveNewGrant">Save</button>
        </div>
      </div>
    </div>

    <div v-for="(grant, index) in localForm.grants" :key="`grant-${grantKey(grant, index)}`" class="grant-item mb-4 p-3 border rounded">
        <div
          class="grant-header d-flex justify-content-between align-items-center"
          role="button"
          tabindex="0"
          @click="toggleGrant(grantKey(grant, index))"
          @keydown.enter.prevent="toggleGrant(grantKey(grant, index))"
          @keydown.space.prevent="toggleGrant(grantKey(grant, index))"
        >
          <h6 class="mb-0 border-0 pb-0">{{ grantHeaderTitles[grantKey(grant, index)] }}</h6>
          <i class="bi" :class="isGrantExpanded(grantKey(grant, index)) ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
        </div>
        <div v-if="isGrantExpanded(grantKey(grant, index))" class="row my-3">
        <div class="col-md-12">
          <label class="form-label">Title</label>
          <input
            type="text"
            class="form-control"
            :class="{ 'field-invalid': isGrantFieldInvalid(grant, 'title', index) }"
            v-model="grant.title"
            placeholder="Grant title"
            @input="markGrantDirty(index)"
          >
        </div>
      </div>
      <div v-if="isGrantExpanded(grantKey(grant, index))" class="row mb-3">
        <div class="col-md-4">
          <label class="form-label">Code</label>
          <input
            type="text"
            class="form-control"
            :class="{ 'field-invalid': isGrantFieldInvalid(grant, 'code', index) }"
            v-model="grant.code"
            placeholder="Grant code"
            @input="markGrantDirty(index)"
          >
        </div>
        <div class="col-md-4">
          <label class="form-label">Select role...</label>
          <select
            class="form-select"
            :class="{ 'field-invalid': isGrantFieldInvalid(grant, 'role', index) }"
            v-model="grant.role"
            @change="markGrantDirty(index)"
          >
            <option value="">Select role...</option>
            <option value="Principal Investigator (PI)">Principal Investigator (PI)</option>
            <option value="Co-Investigator(s)">Co-Investigator(s)</option>
            <option value="Clinical Research Coordinator (CRC)">Clinical Research Coordinator (CRC)</option>
            <option value="Clinical Research Associate (CRA)">Clinical Research Associate (CRA)</option>
            <option value="Data Manager">Data Manager</option>
            <option value="Biostatistician">Biostatistician</option>
            <option value="Regulatory Affairs Specialist">Regulatory Affairs Specialist</option>
            <option value="Clinical Trial Manager">Clinical Trial Manager</option>
            <option value="Clinical Trial Monitor">Clinical Trial Monitor</option>
            <option value="Pharmacist">Pharmacist</option>
            <option value="Patient Recruiter/Enroller">Patient Recruiter/Enroller</option>
            <option value="Quality Assurance Specialist">Quality Assurance Specialist</option>
            <option value="Study Nurse">Study Nurse</option>
            <option value="Other">Other</option>
          </select>
        </div>
        <div class="col-md-4">
          <label class="form-label">Amount</label>
          <input
            type="number"
            class="form-control"
            v-model.number="grant.amount"
            placeholder="0.00"
            step="0.01"
            @input="markGrantDirty(index)"
          >
        </div>
      </div>
      <div v-if="isGrantExpanded(grantKey(grant, index))" class="row mb-3">
        <div class="col-md-12">
          <label class="form-label">Source of funding</label>
          <select
            class="form-select"
            :class="{ 'field-invalid': isGrantFieldInvalid(grant, 'fundingSource', index) }"
            :value="getFundingSourceSelectValue(grant)"
            @change="handleFundingSourceSelectChange(grant, $event.target.value, index)"
          >
            <option value="">Select funding source</option>
            <option
              v-for="option in fundingSourceOptions"
              :key="option"
              :value="option"
            >
              {{ option }}
            </option>
            <option value="__other__">Other</option>
          </select>
          <input
            v-if="isFundingSourceOther(grant)"
            type="text"
            class="form-control mt-2"
            :class="{ 'field-invalid': isGrantFieldInvalid(grant, 'fundingSource', index) }"
            v-model="grant.fundingSource"
            placeholder="Specify other funding source"
            @input="markGrantDirty(index)"
          >
          <small v-if="isFundingSourceOther(grant)" class="text-muted d-block mt-1">
            Enter the funding source name for the Other option.
          </small>
        </div>
      </div>
      <div v-if="isGrantExpanded(grantKey(grant, index))" class="row mb-3">
        <div class="col-md-4">
          <label class="form-label">Currency</label>
          <select
            class="form-select"
            :class="{ 'field-invalid': isGrantFieldInvalid(grant, 'currency', index) }"
            v-model="grant.currency"
            @change="markGrantDirty(index)"
          >
            <option value="">Select currency</option>
            <option value="EUR">EUR</option>
            <option value="USD">USD</option>
            <option value="GBP">GBP</option>
          </select>
        </div>
        <div class="col-md-4">
          <label class="form-label">Start year</label>
          <input
            type="number"
            class="form-control"
            :class="{ 'field-invalid': isGrantFieldInvalid(grant, 'startYear', index) }"
            v-model.number="grant.startYear"
            :min="1900"
            :max="2100"
            @input="markGrantDirty(index)"
          >
        </div>
        <div class="col-md-4">
          <label class="form-label">End year</label>
          <input
            type="number"
            class="form-control"
            :class="{ 'field-invalid': isGrantFieldInvalid(grant, 'endYear', index) }"
            v-model.number="grant.endYear"
            :min="1900"
            :max="2100"
            @input="markGrantDirty(index)"
          >
        </div>
      </div>
      <div v-if="isGrantExpanded(grantKey(grant, index))" class="row">
        <div class="col-md-12 d-flex justify-content-end gap-2">
          <button type="button" class="btn btn-sm btn-outline-primary" :disabled="!grantDirty[index]" @click="saveGrant(index)">
            <i class="bi bi-check me-1"></i>Save
          </button>
          <button type="button" class="btn btn-sm btn-outline-danger" @click="removeGrant(index)">
            <i class="bi bi-x me-1"></i>Remove
          </button>
        </div>
      </div>
    </div>

    <!-- Show message when no grants -->
    <div v-if="localForm.grants.length === 0" class="text-center py-4 text-muted">
      <i class="bi bi-award fs-1 mb-3"></i>
      <p>No grants added yet. Click the ADD button to add your first grant.</p>
    </div>
  </div>
</template>

<script>
import ProfileApiService from '@/services/profile/ProfileApiService'

export default {
  name: 'GrantsForm',
  props: {
    formData: {
      type: Object,
      required: true
    },
    profileData: {
      type: Object,
    }
  },
  emits: ['update', 'save', 'pending-state-change'],
  data() {
    return {
      localForm: this.initializeLocalForm(),
      expandedGrantIds: [],
      grantDraft: null,
      grantDraftAttempted: false,
      grantDirty: {},
      grantAttempted: {},
      newGrantTitleInput: null,
      showDebug: false,
      fundingSourceOptions: [
        "Africa Research Excellence Fund (AREF)",
        "African Academy of Sciences (AAS)",
        "Alexander von Humboldt Foundation",
        "amfAR, The Foundation for AIDS Research",
        "ANESVAD Foundation",
        "Arts and Humanities Research Council (AHRC)",
        "AstraZeneca",
        "Belgian Directorate-General for Development Cooperation and Humanitarian Aid (DGD)",
        "Bill & Melinda Gates Foundation (BMGF)",
        "Canadian Institutes of Health Research (CIHR)",
        "Centers for AIDS Research (CFAR)",
        "Centers for Disease Control and Prevention (CDC)",
        "Centre National de la Recherche Scientifique (CNRS)",
        "Coalition for Epidemic Preparedness Innovations (CEPI)",
        "Danish International Development Agency (Danida)",
        "Department for International Development (DFID)",
        "Deutsche Forschungsgemeinschaft / German Research Foundation (DFG)",
        "Dutch Research Council (NWO)",
        "Else Kröner-Fresenius Foundation",
        "European and Developing Countries Clinical Trials Partnership Association (EDCTP2)",
        "European Commission (EC)",
        "European Malaria Vaccine Development Association (EMVDA)",
        "European Union (EU)",
        "FIND",
        "Fondation Mérieux",
        "FONRID",
        "Gauteng Accelerator Program (GAP)",
        "Gavi, the Vaccine Alliance",
        "German Center for Infection Research (DZIF)",
        "German Federal Ministry of Education and Research (BMBF)",
        "GHIT Fund (Global Health Innovative Technology Fund)",
        "Global Challenges Research Fund (GCRF)",
        "Grand Challenges Canada",
        "IMPRINT / Global Challenges Research Fund (GCRF)",
        "Institut Pasteur",
        "Inserm",
        "ICGEB",
        "International Foundation For Science (IFS)",
        "International Society for Infectious Diseases (ISID)",
        "International Union of Basic and Clinical Pharmacology (IUPHAR)",
        "ISP Healthcare",
        "MasterCard Foundation",
        "Medicines for Malaria Venture (MMV)",
        "Mitsubishi Tanabe Pharma Corporation",
        "National Health Laboratory Service South Africa",
        "National Institute for Health Research (NIHR)",
        "National Institutes of Health (NIH)",
        "National Research Foundation",
        "Novartis Pharma",
        "Novo Nordisk",
        "Poliomyelitis Research Foundation",
        "Rockefeller Foundation",
        "Science for Africa Foundation",
        "South African Medical Research Council (SAMRC)",
        "Sida",
        "Swedish Research Council (SRC)",
        "USAID",
        "UK Newton Fund",
        "UK Medical Research Council (MRC)",
        "UK Royal Society (GCRF)",
        "UNICEF",
        "Wellcome Trust",
        "WHO / TDR"
      ]
    }
  },
  computed: {
    fellowDetailsGrants() {
      return this.profileData?.data?.grants || []
    },
    hasPendingChanges() {
      return Boolean(this.grantDraft)
    },
    grantHeaderTitles() {
      return (this.localForm.grants || []).reduce((titles, grant, index) => {
        const key = this.grantKey(grant, index)
        let code = typeof grant?.code === 'string' ? grant.code.trim().toUpperCase() : ''
        let title = this.normalizeHeaderText(grant?.title)
        let source = this.normalizeHeaderText(grant?.fundingSource)
        const year = this.getYear(grant?.startYear)

        if (!code && !title && !source && !year) {
          titles[key] = `New Grant ${index + 1}`
          return titles
        }

        // Truncate code to 20 characters to prevent line wrapping
        if (code && code.length > 20) {
          code = code.substring(0, 20) + '...'
        }

        // Truncate title to 40 characters to prevent line wrapping in header
        if (title && title.length > 40) {
          title = title.substring(0, 40) + '...'
        }

        // Truncate source/funding source to 35 characters to prevent line wrapping
        if (source && source.length > 35) {
          source = source.substring(0, 35) + '...'
        }

        titles[key] = [code, title, source, year].filter(Boolean).join(' | ')
        return titles
      }, {})
    }
  },
  watch: {
    formData: {
      handler(newData) {
        this.localForm = this.initializeLocalForm(newData)
        this.initializeExpandedGrantIds()
      },
      deep: true,
      immediate: false // Changed to false to avoid double initialization
    },
    profileData: {
      handler(newData) {
        if (newData) {
          // Only reinitialize if we don't have any grants yet
          if (!this.localForm.grants || this.localForm.grants.length === 0) {
            this.localForm = this.initializeLocalForm(this.formData)
            this.initializeExpandedGrantIds()
          }
        }
      },
      deep: true,
      immediate: false
    },
    hasPendingChanges: {
      handler(isPending) {
        this.$emit('pending-state-change', isPending)
      },
      immediate: true
    }
  },
  methods: {
    initializeLocalForm(data = null) {
      const sourceData = data || this.formData

      // Priority 1: Use existing form data if it has grants
      if (sourceData?.grants && Array.isArray(sourceData.grants) && sourceData.grants.length > 0) {
        return {
          grants: sourceData.grants.map(grant => ({
            id: grant.id || null,
            _localKey: grant.id ? null : `${Date.now()}-${Math.random()}`,
            _isNew: false,
            fundingSource: grant.fundingSource || grant.source_funding || '',
            fundingSourceIsOther: false,
            title: grant.title || '',
            code: grant.code || '',
            role: grant.role || '',
            amount: grant.amount || 0,
            currency: grant.currency || '',
            startYear: grant.startYear || grant.start_year || '',
            endYear: grant.endYear || grant.end_year || '',
            isEdctpFellowship: grant.isEdctpFellowship || false,
            fellowshipToLink: grant.fellowshipToLink || ''
          }))
        }
      }

      // Priority 2: Extract from API response if available
      const fellowDetailsGrants = this.fellowDetailsGrants
      if (Array.isArray(fellowDetailsGrants) && fellowDetailsGrants.length > 0) {
        return {
          grants: fellowDetailsGrants.map(grant => ({
            id: grant.id || null,
            _localKey: grant.id ? null : `${Date.now()}-${Math.random()}`,
            _isNew: false,
            fundingSource: grant.fundingSource || grant.source_funding || '',
            fundingSourceIsOther: false,
            title: grant.title || '',
            code: grant.code || '',
            role: grant.role || '',
            amount: grant.amount || 0,
            currency: grant.currency || '',
            startYear: grant.startYear || grant.start_year || '',
            endYear: grant.endYear || grant.end_year || '',
            isEdctpFellowship: grant.isEdctpFellowship || false,
            fellowshipToLink: grant.fellowshipToLink || ''
          }))
        }
      }
      return {
        grants: []
      }
    },

    updateParent() {
      const sanitizedGrants = (this.localForm.grants || []).map(({ fundingSourceIsOther, _localKey, _isNew, ...grant }) => {
        const canonicalFundingSource = this.resolveFundingSourceOption(grant.fundingSource)
        return {
          ...grant,
          // Ensure numeric fields are actual numbers, not strings
          amount: grant.amount ? Number(grant.amount) : null,
          startYear: grant.startYear ? Number(grant.startYear) : null,
          endYear: grant.endYear ? Number(grant.endYear) : null,
          fundingSource: canonicalFundingSource || String(grant.fundingSource || '').trim()
        }
      })

      this.$emit('update', {
        ...this.localForm,
        grants: sanitizedGrants
      })
    },

    addGrant() {
      if (this.grantDraft) {
        this.$nextTick(() => {
          this.$refs.newGrantTitleInput?.focus()
        })
        return
      }

      this.grantDraft = {
        id: null,
        _localKey: `${Date.now()}-${Math.random()}`,
        _isNew: true,
        title: '',
        code: '',
        role: '',
        amount: 0,
        currency: '',
        fundingSource: '',
        fundingSourceIsOther: false,
        startYear: '',
        endYear: '',
        isEdctpFellowship: false,
        fellowshipToLink: ''
      }
      this.grantDraftAttempted = false

      this.$nextTick(() => {
        this.$refs.newGrantTitleInput?.focus()
      })
    },

    cancelNewGrant() {
      this.grantDraft = null
      this.grantDraftAttempted = false
    },

    grantHasRequiredFundingSource(grant) {
      if (!grant) return false

      if (this.isFundingSourceOther(grant)) {
        return Boolean(String(grant.fundingSource || '').trim())
      }

      return Boolean(String(grant.fundingSource || '').trim())
    },

    isGrantFieldInvalid(grant, field, index = null, isDraft = false) {
      const shouldValidate = isDraft ? this.grantDraftAttempted : Boolean(this.grantAttempted[index])
      if (!shouldValidate) return false

      switch (field) {
        case 'title':
          return !String(grant?.title || '').trim()
        case 'code':
          return !String(grant?.code || '').trim()
        case 'role':
          return !String(grant?.role || '').trim()
        case 'fundingSource':
          return !this.grantHasRequiredFundingSource(grant)
        case 'currency':
          return !String(grant?.currency || '').trim()
        case 'startYear':
          return !String(grant?.startYear || '').trim()
        case 'endYear':
          return !String(grant?.endYear || '').trim() || this.hasInvalidYearRange(grant)
        default:
          return false
      }
    },

    hasInvalidYearRange(grant) {
      if (!grant?.startYear || !grant?.endYear) return false
      return Number(grant.startYear) > Number(grant.endYear)
    },

    handleGrantDraftInput() {
      if (this.grantDraftAttempted && this.isGrantValid(this.grantDraft)) {
        this.grantDraftAttempted = false
      }
    },

    isGrantValid(grant) {
      if (!grant) return false

      const hasFundingSource = this.isFundingSourceOther(grant)
        ? Boolean(String(grant.fundingSource || '').trim())
        : Boolean(String(grant.fundingSource || '').trim())

      return Boolean(
        String(grant.title || '').trim() &&
        String(grant.code || '').trim() &&
        String(grant.role || '').trim() &&
        hasFundingSource &&
        String(grant.currency || '').trim() &&
        String(grant.startYear || '').trim() &&
        String(grant.endYear || '').trim() &&
        !this.hasInvalidYearRange(grant)
      )
    },

    async saveNewGrant() {
      if (!this.isGrantValid(this.grantDraft)) {
        this.grantDraftAttempted = true
        if (this.hasInvalidYearRange(this.grantDraft)) {
          this.$emit('error', 'Grant end year must be the same as or after the start year.')
        }
        return
      }

      try {
        this.localForm.grants.unshift({ ...this.grantDraft })
        const grantsData = { grants: this.localForm.grants }
        const response = await ProfileApiService.updateProfile(grantsData)

        // Refresh form data with latest from API response
        if (response?.data?.grants) {
          this.localForm.grants = response.data.grants
        }

        const key = this.grantKey(this.localForm.grants[0], 0)
        if (key && !this.expandedGrantIds.includes(key)) {
          this.expandedGrantIds.push(key)
        }
        this.grantDraft = null
        this.grantDraftAttempted = false
        this.updateParent()
      } catch (error) {
        console.error('Error saving new grant:', error)
        this.$emit('error', 'Failed to save grant. Please try again.')
        // Revert the optimistic update
        this.localForm.grants.shift()
      }
    },

    markGrantDirty(index) {
      this.grantDirty = {
        ...this.grantDirty,
        [index]: true
      }

      if (this.grantAttempted[index] && this.isGrantValid(this.localForm.grants[index])) {
        this.grantAttempted = {
          ...this.grantAttempted,
          [index]: false
        }
      }
    },

    async saveGrant(index) {
      if (!this.isGrantValid(this.localForm.grants[index])) {
        this.grantAttempted = {
          ...this.grantAttempted,
          [index]: true
        }
        if (this.hasInvalidYearRange(this.localForm.grants[index])) {
          this.$emit('error', 'Grant end year must be the same as or after the start year.')
        }
        return
      }

      try {
        const grantsData = { grants: this.localForm.grants }
        const response = await ProfileApiService.updateProfile(grantsData)

        // Refresh form data with latest from API response
        if (response?.data?.grants) {
          this.localForm.grants = response.data.grants
        }

        this.grantDirty = {
          ...this.grantDirty,
          [index]: false
        }
        this.grantAttempted = {
          ...this.grantAttempted,
          [index]: false
        }
        this.updateParent()
        this.$emit('save')
      } catch (error) {
        console.error('Error saving grant:', error)
        this.$emit('error', 'Failed to save grant. Please try again.')
      }
    },

    async removeGrant(index) {
      const removed = this.localForm.grants[index]
      const removedKey = this.grantKey(removed, index)

      this.localForm.grants.splice(index, 1)
      this.expandedGrantIds = this.expandedGrantIds.filter((id) => id !== removedKey)
      const nextDirty = {}
      Object.keys(this.grantDirty).forEach((key) => {
        const dirtyIndex = Number(key)
        if (dirtyIndex < index) {
          nextDirty[dirtyIndex] = this.grantDirty[key]
        }
        if (dirtyIndex > index) {
          nextDirty[dirtyIndex - 1] = this.grantDirty[key]
        }
      })
      this.grantDirty = nextDirty
      const nextAttempted = {}
      Object.keys(this.grantAttempted).forEach((key) => {
        const attemptedIndex = Number(key)
        if (attemptedIndex < index) {
          nextAttempted[attemptedIndex] = this.grantAttempted[key]
        }
        if (attemptedIndex > index) {
          nextAttempted[attemptedIndex - 1] = this.grantAttempted[key]
        }
      })
      this.grantAttempted = nextAttempted

      try {
        const grantsData = { grants: this.localForm.grants }
        const response = await ProfileApiService.updateProfile(grantsData)

        if (response?.data?.grants) {
          this.localForm.grants = response.data.grants
        }

        this.updateParent()
        this.$emit('save')
      } catch (error) {
        console.error('Error removing grant:', error)
        this.$emit('error', 'Failed to remove grant. Please try again.')
        // Revert the optimistic removal
        this.localForm.grants.splice(index, 0, removed)
        this.updateParent()
      }
    },

    grantKey(grant, index) {
      if (!grant) return `new-${index}`
      return grant.id || grant._localKey || `new-${index}`
    },

    toggleGrant(key) {
      if (!key) return

      if (this.expandedGrantIds.includes(key)) {
        this.expandedGrantIds = this.expandedGrantIds.filter((id) => id !== key)
      } else {
        this.expandedGrantIds.push(key)
      }
    },

    isGrantExpanded(key) {
      return this.expandedGrantIds.includes(key)
    },

    initializeExpandedGrantIds() {
      this.expandedGrantIds = (this.localForm.grants || [])
        .map((grant, index) => (grant._isNew ? this.grantKey(grant, index) : null))
        .filter(Boolean)
    },

    normalizeHeaderText(value) {
      if (typeof value !== 'string') return ''
      const cleaned = value.trim().replace(/\s+/g, ' ')
      if (!cleaned) return ''

      return cleaned
        .toLowerCase()
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    },

    getYear(value) {
      if (value === null || value === undefined) return ''
      const asString = String(value).trim()
      const match = asString.match(/^(\d{4})/)
      return match ? match[1] : ''
    },

    // Helpers //

    normalizeGrantRole(value) {
      if (!value) return "";

      const v = String(value).trim().toLowerCase();

      const map = {
        "1": "Principal Investigator (PI)",
        "2": "Co-Investigator(s)",
        "3": "Clinical Research Coordinator (CRC)",
        "4": "Clinical Research Associate (CRA)",
        "5": "Data Manager",
        "6": "Biostatistician",
        "7": "Regulatory Affairs Specialist",
        "8": "Clinical Trial Manager",
        "9": "Clinical Trial Monitor",
        "10": "Pharmacist",
        "11": "Patient Recruiter/Enroller",
        "12": "Quality Assurance Specialist",
        "13": "Study Nurse",
        "14": "Other"
      };

      // If numeric and exists
      if (map[v]) return map[v];

      // Otherwise return original value if it's already a string label
      return value;
    },

    normalizeFundingSource(value) {
      // 1) No value → empty
      if (!value) return "";

      // 2) If array → return first valid item
      if (Array.isArray(value)) {
        return value.length ? this.normalizeFundingSource(value[0]) : "";
      }

      // 3) If object → try known patterns (value/meta/label)
      if (typeof value === "object") {
        if (value.value) return this.normalizeFundingSource(value.value);
        if (value.meta) return this.normalizeFundingSource(value.meta);
        if (value.label) return this.normalizeFundingSource(value.label);
        return "";
      }

      // 4) Now it's safe to treat it as string
      const cleanValue = String(value).trim().replace(/\s+/g, ' ');
      const v = cleanValue.toLowerCase();

      // 4a) Case-insensitive exact match against canonical list (most reliable)
      const canonical = this.getFundingSourceOptions();
      const exactMatch = canonical.find(opt => opt.toLowerCase() === v);
      if (exactMatch) return exactMatch;

      // Known alias map
      const aliasMap = {
        "bill and melinda gates foundation": "Bill & Melinda Gates Foundation (BMGF)",
        "bill & melinda gates foundation": "Bill & Melinda Gates Foundation (BMGF)",
        "bmgf": "Bill & Melinda Gates Foundation (BMGF)",
        "gates foundation": "Bill & Melinda Gates Foundation (BMGF)",
        "nih": "National Institutes of Health (NIH)",
        "mrc": "UK Medical Research Council (MRC)",
        "samrc": "South African Medical Research Council (SAMRC)",
        "welcome trust": "Wellcome Trust",
        "unicef": "UNICEF",
        "usaid": "USAID",
        "edctp": "European and Developing Countries Clinical Trials Partnership Association (EDCTP2)",
        "edctp2": "European and Developing Countries Clinical Trials Partnership Association (EDCTP2)",
      };

      if (aliasMap[v]) return aliasMap[v];

      // 5) Fuzzy match: option contains the input (not the reverse, to avoid short-option false positives)
      if (v.length >= 4) {
        const match = canonical.find(opt => opt.toLowerCase().includes(v));
        if (match) return match;
      }

      // 6) Fallback: return original clean string
      return cleanValue;
    },

    resolveFundingSourceOption(value) {
      if (!value) {
        return ''
      }

      const normalizedValue = this.normalizeFundingSource(value)
      return this.getFundingSourceOptions().find(
        (option) => option.toLowerCase() === String(normalizedValue).trim().toLowerCase()
      ) || ''
    },

    getFundingSourceOptions() {
      return Array.isArray(this.fundingSourceOptions) ? this.fundingSourceOptions : []
    },

    isCustomFundingSource(value) {
      return !!value && !this.resolveFundingSourceOption(value)
    },

    isFundingSourceOther(grant) {
      if (this.resolveFundingSourceOption(grant?.fundingSource)) {
        return false
      }

      return !!grant?.fundingSourceIsOther || this.isCustomFundingSource(grant?.fundingSource)
    },

    getFundingSourceSelectValue(grant) {
      const canonicalValue = this.resolveFundingSourceOption(grant?.fundingSource)
      if (canonicalValue) {
        return canonicalValue
      }

      if (!grant?.fundingSource) {
        return grant?.fundingSourceIsOther ? '__other__' : ''
      }

      return '__other__'
    },

    handleFundingSourceSelectChange(grant, selectedValue, index = null) {
      if (selectedValue === '__other__') {
        grant.fundingSourceIsOther = true
        if (this.getFundingSourceOptions().includes(grant.fundingSource)) {
          grant.fundingSource = ''
        }
      } else {
        grant.fundingSourceIsOther = false
        grant.fundingSource = selectedValue
      }

      if (index !== null) {
        this.markGrantDirty(index)
      }
    },
  },

  mounted() {
    this.initializeExpandedGrantIds()
  }
}
</script>

<style scoped>
.form-step {
  min-height: 400px;
}
.form-label {
  font-weight: var(--font-weight-medium);
  margin-bottom: 0.5rem;
}
.form-control,
.form-select {
  border-radius: 6px;
  border: 1px solid #dee2e6;
  padding: 0.75rem;
}
.form-control:focus,
.form-select:focus {
  border-color: #17a2b8;
  box-shadow: 0 0 0 0.2rem rgba(23, 162, 184, 0.25);
}
.grant-item {
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
}
h6 {
  color: #495057;
  border-bottom: 1px solid #dee2e6;
  padding-bottom: 0.5rem;
  font-weight: var(--font-weight-regular);
  font-size: var(--font-size-base);
}
.btn-outline-danger {
  border-color: #dc3545;
  color: #dc3545;
}
.btn-outline-danger:hover {
  background-color: #dc3545;
  color: white;
}
.form-check-input {
  margin-top: 0.25rem;
}
.form-check-label {
  margin-left: 0.5rem;
  color: #495057;
}
.text-muted {
  font-size: var(--font-size-sm);
  color: #6c757d !important;
}

.section-header {
  margin: 2rem 0 1.5rem 0;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid #17a2b8;
}

.section-title {
  color: #17a2b8;
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-md);
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
</style>
